import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { TextField } from '@material-ui/core'
import { GetAllProducts } from 'api/graphQL/queries/productSearch.queries'
import { ProductSearchResponse } from 'api/graphQL/types/product.types'
import clsx from 'clsx'
import React, {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    stripDoubleSpaces,
    stripDoubleSpacesTrimStart,
} from 'shared/utils/inputUtils'
import { PRODUCT_NAME_MAX_LENGTH } from './AddNewProductModal'
import { RadioType } from './AddProductTypes'
import {
    addProductDialogState,
    setProductDialogState,
} from './addProductFormHandler'
import { useAddProductFormStyles } from './formStyles'

const ProductNameInput: FC = () => {
    const classes = useAddProductFormStyles()

    const {
        firmName,
        familyRadio,
        sectionNames,
        sectionNumber,
        sectionTitle,
        categories,
        productType,
        productName,
        errors,
        type,
    } = useReactiveVar(addProductDialogState)

    const [isSnackbarShown, setIsSnackbarShown] = useState(false)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const isProductNameDisabled = useMemo(
        () =>
            !firmName ||
            (familyRadio === RadioType.Existing
                ? !sectionNames
                : !sectionNumber || !sectionTitle || !categories.length) ||
            !productType,
        [
            firmName,
            sectionNames,
            sectionNumber,
            sectionTitle,
            familyRadio,
            categories,
            productType,
        ]
    )

    const [getAllProducts, { data: productsData }] = useLazyQuery<
        ProductSearchResponse
    >(GetAllProducts, {
        displayName: 'GetAllProducts',
        fetchPolicy: 'network-only',
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product search',
                    variant: 'error',
                    id: `AddProduct_ProductSearchError`,
                },
            })
        },
        onCompleted: () => {
            setProductDialogState({
                isProductNameVerified: true,
            })
        },
    })

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (e?.type === 'change' && e.target.value !== productName) {
                if (!!isSnackbarShown) {
                    setIsSnackbarShown(false)
                }

                setProductDialogState({
                    productName: stripDoubleSpacesTrimStart(e.target.value),
                    isProductNameVerified: false,
                    errors: {},
                })

                getAllProducts({
                    variables: {
                        manufacturerProductSearchInput: {
                            facets: [
                                {
                                    name: 'listingLevels',
                                    values: !type
                                        ? ['']
                                        : type === ListingLevel.Premium
                                        ? [
                                              ListingLevel.Manufacturer,
                                              ListingLevel.Premium,
                                              ListingLevel.PremiumMasterSpec,
                                          ]
                                        : [type],
                                },
                                {
                                    name: 'displayNameWithFirmName',
                                    values: [firmName],
                                },
                                {
                                    name: 'sectionNames',
                                    values: [sectionNames],
                                },
                                {
                                    name: 'productType',
                                    values: [productType],
                                },
                                {
                                    name: 'productName',
                                    values: [
                                        stripDoubleSpaces(e?.target?.value) ??
                                            '',
                                    ],
                                },
                            ],
                        },
                    },
                })
            }
        },
        [
            setProductDialogState,
            type,
            sectionNames,
            firmName,
            productType,
            productName,
            isSnackbarShown,
            setIsSnackbarShown,
            errors,
            stripDoubleSpaces,
            stripDoubleSpacesTrimStart,
        ]
    )

    useEffect(() => {
        if (productsData?.listingSearch?.matches?.length && !isSnackbarShown) {
            setIsSnackbarShown(true)

            setProductDialogState({
                errors: {
                    productName: 'Duplicate Product Name',
                },
                isProductNameVerified: false,
            })

            showGlobalSnackbar({
                variables: {
                    message:
                        'Duplicate Product  Name. Please enter a different one',
                    variant: 'error',
                    id: 'AddProduct_Duplicate_ProductName',
                },
            })
        }
    }, [
        productsData,
        setProductDialogState,
        showGlobalSnackbar,
        isSnackbarShown,
        setIsSnackbarShown,
    ])

    return (
        <GridItem
            className={clsx(classes.rowContainer, 'rowContainer')}
            xs={12}
            data-testid="productName"
        >
            <TextField
                className={classes.inputField}
                name="productName"
                fullWidth
                error={!!errors.productName}
                inputProps={{ maxLength: PRODUCT_NAME_MAX_LENGTH }}
                disabled={isProductNameDisabled}
                value={productName}
                onChange={handleChange}
                variant="outlined"
                label="Product Name"
            />
        </GridItem>
    )
}
export default ProductNameInput
